import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { Definition } from '../../../core/store/definitions/definition.interface';
import { pairwise, startWith, takeUntil } from 'rxjs/operators';
import { Destroyable } from '@dlw-int-mwi/eps-common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LayoutBreakpoints } from '../../../core/util/layout-breakpoints';
import { InvoiceQueryFilter, InvoiceQueryRequest } from '../../../core/interfaces/invoice.interface';
import { DateTime } from 'luxon';
import { NotificationService } from '../../../core/services/notification.service';
import { environment } from '../../../../environments/environment';

const luxonFormat = 'yyyy-MM-dd';
const maxMonthsTimespan = environment.invoice.maxMonthsTimespan || 120;
const defaultMonthsTimespan = environment.invoice.defaultMonthsTimespan || 12;

@Component({
  selector: 'invoices-filter',
  templateUrl: './invoices-filter.component.html',
  styleUrls: ['./invoices-filter.component.scss'],
})
export class InvoicesFilterComponent extends Destroyable implements OnInit, OnChanges {
  @Input() loading: boolean;
  @Input() busy: boolean;
  @Input() invoiceTypes: Definition[] = [];
  @Input() soldTos: Customer[];
  @Input() showSelectCustomers: boolean;
  @Input() clearFilter: EventEmitter<void>;
  @Output() filterChanged = new EventEmitter<{filter: InvoiceQueryFilter, valid: boolean}>();

  private breakpointObserver = inject(BreakpointObserver);
  private cd = inject(ChangeDetectorRef);

  filterForm?: FormGroup<FilterForm>;
  showDatesIn2rows$ = LayoutBreakpoints.matches(this.breakpointObserver, ...LayoutBreakpoints.tooSmallFor2Fields);
  defaultValues;
  now = DateTime.now().toFormat(luxonFormat);

  private notificationService = inject(NotificationService);

  constructor() {
    super();
    this.showDatesIn2rows$.pipe(
      takeUntil(this.destroyed$),
    ).subscribe(showDatesIn2rows => {
      this.cd.markForCheck();
    });
  }

  ngOnInit() {
    this.createForm();
    if (this.clearFilter) {
      this.clearFilter.pipe(this.takeUntilDestroyed()).subscribe(() => this.clearFilterForm());
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.soldTos) {
      this.selectFirstSoldToIfEmptyInForm();
    }
  }

  createForm(): void {
    this.filterForm = new FormGroup<FilterForm>({
      soldToId: new FormControl<string>('', Validators.required),
      currency: new FormControl<string>(''),
      invoiceDateFrom: new FormControl<string>(''),
      invoiceDateTo: new FormControl<string>(''),
      invoiceNumber: new FormControl<string>(''),
      invoiceType: new FormControl<string>('', { nonNullable: false }),
      invoiceNetAmountFrom: new FormControl<number>(null),
      invoiceNetAmountTo: new FormControl<number>(null),
    });
    this.selectFirstSoldToIfEmptyInForm();
    this.correctDates({}, this.filterForm.value);
    this.defaultValues = this.filterForm.value;
    this.filterForm.valueChanges.pipe(startWith(this.defaultValues), pairwise()).subscribe(([previous, next]) => this.emitFilter(previous, next));
    this.emitFilter({}, this.defaultValues);
  }

  private emitFilter(previous: Partial<InvoiceQueryRequest>, next: Partial<InvoiceQueryRequest>): void {
    this.selectFirstSoldToIfEmptyInForm();
    this.correctDates(previous, next);
    next = this.filterForm.value;
    const filter: InvoiceQueryFilter = {
      currency: next.currency,
      invoiceDateFrom: next.invoiceDateFrom,
      invoiceDateTo: next.invoiceDateTo,
      invoiceNumber: next.invoiceNumber,
      invoiceNetAmountFrom: next.invoiceNetAmountFrom,
      invoiceNetAmountTo: next.invoiceNetAmountTo,
      invoiceType: next.invoiceType,
      soldToId: next.soldToId
    };
    this.filterChanged.emit({filter, valid: this.filterForm.valid});
  }

  private correctDates(previous?: Partial<InvoiceQueryRequest>, next?: Partial<InvoiceQueryRequest>): void {
    let corrected = false;
    if (!next.invoiceDateFrom && !next.invoiceDateTo) {
      const today = DateTime.now().toFormat(luxonFormat);
      const lastYear = DateTime.now().minus({ months: defaultMonthsTimespan }).plus({ day: 1 }).toFormat(luxonFormat);
      this.filterForm.patchValue({ invoiceDateFrom: lastYear, invoiceDateTo: today });
      return;
    }
    if (!previous) {
      throw new Error('Invalid use of function correctDates. Please provice previous and next');
    }

    const dateFromChanged = previous.invoiceDateFrom !== next.invoiceDateFrom;
    const dateToChanged = previous.invoiceDateTo !== next.invoiceDateTo;

    if (!next.invoiceDateFrom) {
      const dateTo = DateTime.fromFormat(next.invoiceDateTo, luxonFormat);
      const dateFrom = dateTo.minus({ months: maxMonthsTimespan }).plus({ day: 1 });
      this.filterForm.patchValue({ invoiceDateFrom: dateFrom.toFormat(luxonFormat) });
      corrected = true;
    }
    if (!next.invoiceDateTo) {
      const dateFrom = DateTime.fromFormat(next.invoiceDateFrom, luxonFormat);
      let dateTo = dateFrom.plus({ months: maxMonthsTimespan }).minus({ day: 1 });
      if (dateTo.diffNow('days').days > 0) {
        dateTo = DateTime.now();
      }
      this.filterForm.patchValue({ invoiceDateTo: dateTo.toFormat(luxonFormat) });
      corrected = true;
    }

    if (!corrected) {
      const dateFrom = DateTime.fromFormat(next.invoiceDateFrom, luxonFormat);
      const dateTo = DateTime.fromFormat(next.invoiceDateTo, luxonFormat);
      if (dateTo.diff(dateFrom, 'months').months > maxMonthsTimespan) {
        if (dateFromChanged) {
          // change dateTo
          const newDateTo = dateFrom.plus({ months: maxMonthsTimespan }).minus({ day: 1 });
          this.filterForm.patchValue({ invoiceDateTo: newDateTo.toFormat(luxonFormat) });
          corrected = true;
        } else if (dateToChanged) {
          // change dateFrom
          const newDateFrom = dateTo.minus({ months: maxMonthsTimespan }).plus({ day: 1 });
          this.filterForm.patchValue({ invoiceDateFrom: newDateFrom.toFormat(luxonFormat) });
          corrected = true;
        }
      }
    }
    if (corrected) {
      this.notificationService.showToastr({
        messageCode: 'INVOICES.FILTER.INFO.MAX_RANGE',
        translationParams: {months: maxMonthsTimespan},
        type: 'info'
      })
    }
  }

  resetFilter() {
    this.filterForm.setValue(this.defaultValues);
  }

  private clearFilterForm() {
    this.filterForm.setValue(this.defaultValues);
  }

  private selectFirstSoldToIfEmptyInForm() {
    if (this.soldTos?.length && this.filterForm && !this.filterForm.value.soldToId) {
      this.filterForm.patchValue({soldToId: this.soldTos[0].id});
    }
  }
}

interface FilterForm {
  soldToId: FormControl<string>;
  invoiceNumber: FormControl<string>;
  currency: FormControl<string>;
  invoiceType: FormControl<string>;
  invoiceDateFrom: FormControl<string>;
  invoiceDateTo: FormControl<string>;
  invoiceNetAmountFrom: FormControl<number>;
  invoiceNetAmountTo: FormControl<number>;
}
